import {DoWork, runWorker} from 'observable-webworker';
import {Observable, Subject, Subscription} from 'rxjs';
import {StompConnect, StompEvent, StompEventType} from './stomp-event';
import {marshallStompFrame, StompFrame, unmarshallSingleStompFrame} from '../ws/stomp-frame';
import {WebSocketSubject, WebSocketSubjectConfig} from 'rxjs/webSocket';

export class StompWorker implements DoWork<StompEvent, StompEvent> {

    constructor() {
        this.subject = new Subject<StompEvent>();
        this.client = null;
        this.socketSubscription = null;
        this.heartbeatInterval = 0;
        this.heartbeatMargin = 1500;
        this.heartbeatReceiveMargin = this.heartbeatMargin + 8000;
        this.heartbeatIntervalId = null;
        this.isConnecting = false;
    }
    private client: WebSocketSubject<StompFrame> | null;
    private socketSubscription: Subscription | null;
    private subject: Subject<StompEvent>;
    private lastSent!: number;
    private lastReceived!: number;
    private heartbeatInterval: number;
    private readonly heartbeatMargin: number;
    private readonly heartbeatReceiveMargin: number;
    private heartbeatIntervalId: any | null;
    private isConnecting: boolean;
    private inputSubscription: Subscription | null = null;

    private static onDeserialize(e: MessageEvent): StompFrame {
        const stompFrame = unmarshallSingleStompFrame(e.data);
        return stompFrame;
    }

    private static onSerialize(e: StompFrame): string {
        try {
            const result = marshallStompFrame(e);
            return result;
        } catch (serializeError) {
            console.error(serializeError);
            throw serializeError;
        }
    }

    private static onComplete(): void {
    }

    selectTransferables(_output: StompEvent): Transferable[] {
        return [];
    }

    work(input$: Observable<StompEvent>): Observable<StompEvent> {
        this.inputSubscription = input$.subscribe((stompEvent) => {
            switch (stompEvent.type) {
                case StompEventType.OPEN:
                    if (stompEvent.connect) {
                        this.connect(stompEvent.connect);
                    }
                    break;
                case StompEventType.CLOSE:
                    this.client?.complete();
                    this.inputSubscription?.unsubscribe();
                    this.inputSubscription = null;
                    break;
                case StompEventType.FRAME:
                    if (stompEvent.frame) {
                        this.send(stompEvent.frame);
                    }
                    break;
            }
        });
        return this.subject.asObservable();
    }

    private isConnected(): boolean {
        return !!this.client && !this.client.isStopped && !this.client.closed && !!this.socketSubscription && !this.socketSubscription.closed;
    }

    private isNotConnected(): boolean {
        return !this.client || this.client.isStopped || this.client.closed || !this.socketSubscription || this.socketSubscription.closed;
    }

    private connect(stompConnect: StompConnect): void {
        if (this.isNotConnected() && !this.isConnecting) {
            this.heartbeatInterval = stompConnect.heartbeat;
            const wsc: WebSocketSubjectConfig<StompFrame> = {
                url: stompConnect.url,
                serializer: (e) => StompWorker.onSerialize(e),
                deserializer: (e) => StompWorker.onDeserialize(e),
                closeObserver: {
                    next: (closeEvent: CloseEvent) => this.onClose(closeEvent)
                },
                openObserver: {
                    next: (event: Event) => this.onOpen(event)
                }
            };
            this.client = new WebSocketSubject(wsc);
            this.socketSubscription = this.client.subscribe(
                (frame) => this.onFrame(frame),
                (error) => this.onError(error),
                () => StompWorker.onComplete());
            this.clearHeartbeat();
            this.heartbeatIntervalId = setInterval(() => this.heartbeatCheck(), 1000);
        }
    }

    private clearHeartbeat(): void {
        if (this.heartbeatIntervalId) {
            clearInterval(this.heartbeatIntervalId);
            this.heartbeatIntervalId = null;
        }
    }

    private onClose(closeEvent: CloseEvent): void {
        this.isConnecting = false;
        this.client?.unsubscribe();
        this.client = null;
        this.socketSubscription?.unsubscribe();
        this.socketSubscription = null;
        this.clearHeartbeat();
        this.subject.next({
            type: StompEventType.CLOSE,
            close: {
                code: closeEvent.code,
                reason: closeEvent.reason,
                wasClean: closeEvent.wasClean
            }
        });
        if (!this.inputSubscription) {
            this.subject.complete();
        }
    }

    private send(frame: StompFrame): void {
        if (this.isConnected()) {
            this.lastSent = Date.now();
            this.client?.next(frame);
        }
    }

    private heartbeatCheck(): void {
        if (this.heartbeatInterval > 0 && this.isConnected()) {
            const now = Date.now();
            const receivedInterval = this.heartbeatInterval + this.heartbeatReceiveMargin;
            const sentInterval = this.heartbeatInterval - this.heartbeatMargin;
            const lastReceivedDelta = now - this.lastReceived;
            if (lastReceivedDelta > receivedInterval) {
                this.clearHeartbeat();
                this.client?.complete();
                return;
            }
            const lastSentDelta = now - this.lastSent;
            if (lastSentDelta > sentInterval) {
                this.send({});
            }
        }
    }

    private onOpen(event: Event): void {
        if (event.type === 'open') {
            this.isConnecting = false;
            this.subject.next({
                type: StompEventType.OPEN
            });
        }
    }

    private onFrame(frame: StompFrame): void {
        this.lastReceived = Date.now();
        this.subject.next({
            type: StompEventType.FRAME,
            frame: frame
        });
    }

    private onError(error: any): void {
        console.error(error);
        this.clearHeartbeat();
        this.subject.next({
            type: StompEventType.ERROR,
            error: JSON.stringify(error)
        });
    }
}

runWorker(StompWorker);
